/**
 * Петушиный крик, синтезированный на месте.
 *
 * Почему не звуковой файл. Во-первых, запись петуха — чужая работа с чужими
 * правами, и класть её в репозиторий нельзя. Во-вторых, политика безопасности
 * страницы запрещает загрузку чего бы то ни было со стороны, а встроить звук
 * в data-адрес не выйдет по той же причине: default-src 'self' не пускает
 * схему data: для медиа. Синтез не требует ни файла, ни послабления в CSP.
 *
 * Из чего складывается «похоже на петуха», а не на сигнал будильника:
 *
 * 1. Богатый спектр. Пила спадает по гармоникам как 1/n и звучит глухо;
 *    у птичьего крика верхние гармоники почти не слабеют. Волна собирается
 *    вручную со спадом 1/n^0.55.
 * 2. Два форманта, а не один фильтр. Голосовой тракт даёт минимум две области
 *    подъёма; с одной полосой звук остаётся «электронным».
 * 3. Дрожание высоты. Ровная частота — признак генератора, а не живого горла:
 *    на тон наложен медленный случайный увод в пределах четверти тона.
 * 4. Вибрато и тремоло на последнем слоге. Петух заканчивает крик дрожащим
 *    затухающим «у-у», и без этого хвост звучит обрубленным.
 * 5. Мягкое ограничение амплитуды. Крик резкий и слегка хриплый, чистая сумма
 *    синусоид такой не бывает.
 */

/**
 * Слоги «ку-ка-ре-ку».
 *
 * Высоты и длительности подобраны по строению настоящего крика: два коротких
 * слога со скачком вверх, длинный верхний и долгий падающий хвост.
 */
const SYLLABLES = [
  { hz: 520, glideTo: 610, seconds: 0.11, vibrato: 0, tremolo: 0, level: 0.55 },
  { hz: 900, glideTo: 980, seconds: 0.12, vibrato: 0, tremolo: 0, level: 0.8 },
  { hz: 1060, glideTo: 990, seconds: 0.3, vibrato: 30, tremolo: 0, level: 1 },
  { hz: 790, glideTo: 430, seconds: 0.52, vibrato: 45, tremolo: 0.3, level: 0.85 },
];

/** Пауза между слогами. */
const GAP = 0.04;

/** Форманты голосового тракта: нижний даёт «тело», верхний — крикливость. */
const FORMANTS = [
  { hz: 900, q: 4, gain: 0.7 },
  { hz: 2400, q: 6, gain: 1 },
];

/** Размах случайного увода высоты, в центах. Ровный тон звучит синтетически. */
const JITTER_CENTS = 28;

/** Сколько гармоник собирать в волне. Выше 30 всё равно уходит за слышимое. */
const HARMONICS = 30;

type AudioContextConstructor = typeof AudioContext;

/**
 * Контекст один на страницу и создаётся при первом крике.
 *
 * Создавать его заранее нельзя: до действия пользователя браузер держит
 * контекст приостановленным, а счётчик контекстов ограничен — на десятом
 * вызове звук просто перестал бы появляться.
 */
let context: AudioContext | null = null;
let voice: PeriodicWave | null = null;
let curve: Float32Array<ArrayBuffer> | null = null;

function audioContext(): AudioContext | null {
  if (context) return context;
  const Ctor: AudioContextConstructor | undefined =
    window.AudioContext
    ?? (window as unknown as { webkitAudioContext?: AudioContextConstructor }).webkitAudioContext;
  if (!Ctor) return null;
  context = new Ctor();
  return context;
}

/** Волна с медленно спадающими гармониками — основа «голосистого» тембра. */
function voiceWave(ctx: AudioContext): PeriodicWave {
  if (voice) return voice;
  const real = new Float32Array(HARMONICS + 1);
  const imag = new Float32Array(HARMONICS + 1);
  for (let n = 1; n <= HARMONICS; n += 1) {
    // Чередование знака разводит гармоники по фазе: синфазная сумма даёт
    // короткий щелчок вместо непрерывного звука.
    imag[n] = (n % 2 === 0 ? -1 : 1) / Math.pow(n, 0.55);
  }
  voice = ctx.createPeriodicWave(real, imag, { disableNormalization: false });
  return voice;
}

/** Кривая мягкого ограничения: добавляет хрипотцы, не превращая звук в треск. */
function clipCurve(): Float32Array<ArrayBuffer> {
  if (curve) return curve;
  const size = 1024;
  // Буфер создаётся явно: WaveShaper требует массив именно над ArrayBuffer, а
  // не над произвольным хранилищем, и без этого не сходятся типы.
  const shaped = new Float32Array(new ArrayBuffer(size * Float32Array.BYTES_PER_ELEMENT));
  for (let i = 0; i < size; i += 1) {
    const x = (i / (size - 1)) * 2 - 1;
    shaped[i] = Math.tanh(x * 2.2) / Math.tanh(2.2);
  }
  curve = shaped;
  return shaped;
}

/**
 * Медленный случайный сигнал для дрожания высоты.
 *
 * Именно шум, а не синус: ровное колебание слышится как вибрато, тогда как
 * живому горлу свойственна неровность без периода.
 */
function jitterSource(ctx: AudioContext, seconds: number): AudioBufferSourceNode {
  const rate = 60; // отсчётов в секунду — медленное блуждание, а не шипение
  const length = Math.max(2, Math.ceil(seconds * rate));
  const buffer = ctx.createBuffer(1, length, rate);
  const data = buffer.getChannelData(0);
  let value = 0;
  for (let i = 0; i < length; i += 1) {
    value = value * 0.6 + (Math.random() * 2 - 1) * 0.4;
    data[i] = value;
  }
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  return source;
}

/** Короткий шумовой всплеск — придыхание в начале слога. */
function breath(ctx: AudioContext, at: number, target: AudioNode, level: number): void {
  const length = Math.floor(ctx.sampleRate * 0.035);
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i += 1) {
    data[i] = (Math.random() * 2 - 1) * (1 - i / length) ** 2;
  }
  const source = ctx.createBufferSource();
  source.buffer = buffer;

  const gain = ctx.createGain();
  gain.gain.value = 0.09 * level;
  source.connect(gain).connect(target);
  source.start(at);
}

function syllable(
  ctx: AudioContext,
  at: number,
  spec: (typeof SYLLABLES)[number],
  target: AudioNode,
): void {
  const end = at + spec.seconds;

  // Огибающая громкости: резкая атака, плавный спад.
  const envelope = ctx.createGain();
  envelope.gain.setValueAtTime(0.0001, at);
  envelope.gain.exponentialRampToValueAtTime(spec.level, at + 0.018);
  envelope.gain.setValueAtTime(spec.level, at + spec.seconds * 0.35);
  envelope.gain.exponentialRampToValueAtTime(0.0001, end);
  envelope.connect(target);

  // Тремоло — дрожание громкости на затухающем хвосте.
  if (spec.tremolo) {
    const depth = ctx.createGain();
    depth.gain.value = spec.tremolo;
    const lfo = ctx.createOscillator();
    lfo.type = "sine";
    lfo.frequency.value = 17;
    lfo.connect(depth).connect(envelope.gain);
    lfo.start(at);
    lfo.stop(end);
  }

  const shaper = ctx.createWaveShaper();
  shaper.curve = clipCurve();
  shaper.connect(envelope);

  // Два форманта параллельно: сумма двух полос, а не одна.
  for (const formant of FORMANTS) {
    const filter = ctx.createBiquadFilter();
    filter.type = "bandpass";
    filter.frequency.value = formant.hz;
    filter.Q.value = formant.q;
    const level = ctx.createGain();
    level.gain.value = formant.gain;
    filter.connect(level).connect(shaper);

    const osc = ctx.createOscillator();
    osc.setPeriodicWave(voiceWave(ctx));
    osc.frequency.setValueAtTime(spec.hz, at);
    osc.frequency.exponentialRampToValueAtTime(spec.glideTo, end);

    // Дрожание высоты.
    const jitter = jitterSource(ctx, spec.seconds);
    const jitterDepth = ctx.createGain();
    jitterDepth.gain.value = JITTER_CENTS;
    jitter.connect(jitterDepth).connect(osc.detune);
    jitter.start(at);
    jitter.stop(end);

    // Вибрато поверх дрожания — им петух заканчивает крик.
    if (spec.vibrato) {
      const vibratoDepth = ctx.createGain();
      vibratoDepth.gain.value = spec.vibrato;
      const lfo = ctx.createOscillator();
      lfo.type = "sine";
      lfo.frequency.setValueAtTime(24, at);
      lfo.frequency.linearRampToValueAtTime(15, end);
      lfo.connect(vibratoDepth).connect(osc.detune);
      lfo.start(at);
      lfo.stop(end);
    }

    osc.connect(filter);
    osc.start(at);
    osc.stop(end + 0.02);
  }

  breath(ctx, at, shaper, spec.level);
}

/**
 * Кукарекает. Вызывать только из обработчика действия пользователя: без жеста
 * браузер не разрешит воспроизведение и контекст останется приостановленным.
 */
export function crow(): void {
  const ctx = audioContext();
  if (!ctx) return;
  void ctx.resume();

  const master = ctx.createGain();
  master.gain.value = 0.3;
  master.connect(ctx.destination);

  let at = ctx.currentTime + 0.03;
  for (const spec of SYLLABLES) {
    syllable(ctx, at, spec, master);
    at += spec.seconds + GAP;
  }
}
